import { Box, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ajaxService from "../services/ajax-service";
import { ImageURL, SITE_URL } from "../constants";
import { ProductSlider, Content, Description, Popular, BrandBanner, AlternativeProducts } from "../components";
import ProductViewSkeleton from "../components/Product/Skeleton";
import { useTranslation } from "react-i18next";
import { getDescription } from "../utils";
import SEO from "../components/SEO";
import defaultImage from "../assets/contactsvg.svg";
import { Notification } from "../components/AddToWishlist";
import DeliveryTags from "../components/Product/DeliveryTags";
import RecentlyView from "../components/RecentlyView";

const Product = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState({});
  const [images, setImages] = useState([]);
  const [wishlistNotify, setWishlistNotify] = useState(false);
  const { t, i18n } = useTranslation();

  // Function to strip HTML tags and decode HTML entities
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const loadData = async (slug) => {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      let url = `/product/single-product/${slug}`;

      if (token && user) {
        const userData = JSON.parse(user);
        url += `?user_id=${userData.id}`;
      }

      const response = await ajaxService.get(url);
      if (response.success) {
        setProduct(response.data);
        const productImages = response.data.images && response.data.images.length > 0
          ? response.data.images.map((i) => ImageURL + i)
          : [defaultImage];
        setImages(productImages);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading product data:", error);
      setLoading(false);
    }
  };

 
useEffect(() => {
  console.log("PRODUCT LOAD START:", slug);

  loadData(slug);
}, [slug]);

useEffect(() => {
  if (
    product?.code &&
    typeof window !== 'undefined' &&
    typeof window.fbq === 'function'
  ) {
    console.log("META ViewContent firing:", product.code);

    window.fbq('track', 'ViewContent', {
      content_ids: [String(product.code)],
      content_name: product.name,
      content_type: 'product',
      currency: product.currency || 'AED',
      value: Number(product.discount_price ?? product.price),
    });
  }
}, [product?.code]);



  return (
    <Box className="w-full h-auto bg-white m-0 p-0">
      {!loading && product && Object.keys(product).length > 0 && (
        <SEO
          title={`${product.name} in UAE | ${product.brand} | Buraq Star`}
          description={
  product.description
    ? `${product.name} by ${product.brand} available in UAE from Buraq Star. ${stripHtml(product.description).substring(0, 110)}`
    : `Buy ${product.name} by ${product.brand} in UAE from Buraq Star. Quality electrical products with competitive prices and UAE delivery.`
}
          keywords={[
  product.name,
  product.brand,
  `${product.name} UAE`,
  `${product.brand} UAE`,
  `${product.name} in UAE`,
  product.category?.name,
  `${product.category?.name || ''} UAE`,
  "electrical products UAE",
  "electrical supplier UAE",
  "Buraq Star"
].filter(Boolean).join(", ")}
          image={
            product.images && product.images.length > 0
              ? ImageURL + product.images[0]
              : `${SITE_URL}/buraq-banner1.png`
          }
          url={`/product/${slug}`}
          canonical={`/product/${slug}`}
          type="product"

          structuredData={[
  {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description
      ? stripHtml(product.description)
      : `Premium ${product.name} from ${product.brand}`,
    "image": product.images ? product.images.map(img => ImageURL + img) : [],
    "brand": { "@type": "Brand", "name": product.brand },
    "sku": product.sku || String(product.id),
    "mpn": product.sku || String(product.id),
    "category": product.category?.name || "Electrical Products",
    "offers": {
      "@type": "Offer",
      "price": product.discount_price ?? product.price ?? "0",
      "priceCurrency": "AED",
      "availability": product.stocks > 0
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
      "url": `${SITE_URL}/product/${slug}`,
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0],
      "seller": {
        "@type": "Organization",
        "name": "Buraq Star Trading"
      }
    },
    // Add when you have ratings:
    ...(product.rating && product.review_count && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": String(product.rating),
        "reviewCount": String(product.review_count),
        "bestRating": "5",
        "worstRating": "1"
      }
    })
  },
  {
    // BreadcrumbList — shows "buraqstar.com › Electrical › Fans" in Google
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      ...(product.category?.parent_name ? [{
        "@type": "ListItem",
        "position": 2,
        "name": product.category.parent_name,
        "item": `${SITE_URL}/category/${product.category.parent_slug}`
      }] : []),
      ...(product.category?.name ? [{
        "@type": "ListItem",
        "position": product.category?.parent_name ? 3 : 2,
        "name": product.category.name,
        "item": `${SITE_URL}/category/${product.category.parent_slug || ''}/${product.category.slug || ''}`
      }] : []),
      {
        "@type": "ListItem",
        "position": product.category?.parent_name ? 4 : 3,
        "name": product.name
      }
    ]
  }
]}
        />
      )}

      {/* Breadcrumb */}
      <Typography
        className="text-gray-700 leading-relaxed poppins"
        sx={{
          ml: { xs: 2, sm: 4, md: 5 },
          pl: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 3, sm: 4, md: 5 },
          fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.125rem' },
        }}
      >
        {t('dashboard_sidebar.home')} &nbsp; &gt; &nbsp;
        <span className="text-[#2858A3]">{t('Product Detail')}</span>
      </Typography>

      {loading ? (
        <ProductViewSkeleton />
      ) : (
        <Grid
          container
          sx={{
            // Mobile: tight padding; mini-iPad (≥600px): medium; tablet (≥900px): more; desktop (≥1200px): full
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
            py: { xs: 2, sm: 4, md: 6, lg: 8 },
            pt: { xs: 2, sm: 2, md: 2, lg: 0 },
            columnSpacing: { xs: 0, sm: 2, md: 3, lg: 4 },
            rowSpacing: { xs: 2, sm: 3, md: 3 },
          }}
        >
          {/* Product Image Slider */}
          <Grid
            item
            xs={12}   // mobile: full width
            sm={5}    // mini-iPad (600–899px): 5/12
            md={4}    // tablet (900–1199px): 4/12
            lg={4}    // desktop: 4/12
            className="animate-fadeInLeft"
            sx={{
              animationDuration: "0.6s",
              animationDelay: "0.2s",
              animationFillMode: "forwards",
            }}
          >
            <ProductSlider images={images} />
          </Grid>

          {/* Product Content / Details */}
          <Grid
            item
            xs={12}   // mobile: full width stacked
            sm={7}    // mini-iPad: 7/12
            md={8}    // tablet: 8/12
            lg={6}    // desktop: 6/12
            className="animate-fadeInLeft"
            sx={{
              pt: { xs: 2, sm: 0 },
              animationDuration: "0.8s",
              animationDelay: "0.4s",
              animationFillMode: "forwards",
            }}
          >
            <Box
              sx={{
                position: { xs: 'static', md: 'sticky' },
                top: { md: 20 },
              }}
            >
              <Content
                product={product}
                setProduct={setProduct}
                t={t}
                wishlistNotify={wishlistNotify}
                setWishlistNotify={setWishlistNotify}
              />
            </Box>
          </Grid>

          {/* Description / Specifications — always full width */}
          <Grid
            item
            xs={12}
            className="animate-fadeInLeft"
            sx={{
              animationDuration: "1s",
              animationDelay: "0.6s",
              animationFillMode: "forwards",
            }}
          >
            <Description
              description={getDescription(product, i18n.language)}
              specifications={product?.specifications}
              brand={product?.brand}
              datasheet={product?.datasheet}
              t={t}
            />
          </Grid>

          {/* Reserved / future grid item */}
          <Grid
            item
            xs={12}
            className="animate-fadeInLeft"
            sx={{
              animationDuration: "1.2s",
              animationDelay: "0.8s",
              animationFillMode: "forwards",
            }}
          >
            {/* Future content here */}
          </Grid>
        </Grid>
      )}

      {/* Recently Viewed */}
      <Grid
        item
        xs={12}
        className="animate-fadeInLeft"
        sx={{
          animationDuration: "1s",
          animationDelay: "1.2s",
          animationFillMode: "forwards",
        }}
      >
        <RecentlyView />
      </Grid>

      {/* Delivery Tags */}
      <Grid
        item
        xs={12}
        className="animate-fadeInLeft"
        sx={{
          animationDuration: "1.2s",
          animationDelay: "1.2s",
          animationFillMode: "forwards",
        }}
      >
        <Box
        className='mt-2'
          sx={{
            pb: { xs: 4, sm: 6, md: 8 },
            px: { xs: 1, sm: 2, md: 2 },
            mt: { xs: 0, sm: -2, md: -4 },
          }}
        >
          <DeliveryTags />
        </Box>
      </Grid>

      {/* Wishlist Notification — fixed top-right */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 60, sm: 70, md: 80 },
          right: { xs: 10, sm: 16, md: 20 },
          zIndex: 9999,
        }}
      >
        <Notification open={wishlistNotify} setOpen={setWishlistNotify} />
      </Box>
    </Box>
  );
};

export default Product;